import {
  Quotation,
  QuotationUnit,
  Vehicle,
  Driver,
  Customer,
  QuotationCoverage,
  CoverageCatalogResume,
  QuotationSummaryResponse,
} from 'data-layer-cr/dist/models';
import { makeEmptyCustomer } from 'data-layer-cr/dist/models/Customer';
import { makeEmptyDriver } from 'data-layer-cr/dist/models/Driver';
import { makeEmptyQuotation } from 'data-layer-cr/dist/models/Quotation';
import { makeEmptyQuotationUnit } from 'data-layer-cr/dist/models/QuotationUnit';
import { makeEmptyVehicle } from 'data-layer-cr/dist/models/Vehicle';
import { NATURAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import {
  THIRD_PARTY_PACK,
  COMPLETE_VEHICLE,
} from 'data-layer-cr/dist/constants/products';
import { uniqueId } from 'lodash';

import { defaultUnitEntity } from 'data-layer-cr/dist/functions/quotations';

import {
  GOLD_ENDORSEMENT,
  PLAT_ENDORSEMENT,
  PLAT_PLUS_ENDORSEMENT,
} from 'src/constants/endorsementCodes';
import CustomerItem from '../models/helperTypes/CustomerItem';
import CarItem from '../models/helperTypes/CarItem';

export type UniqueIdUnit = {
  id: string;
  unit: QuotationUnit;
};

export function makeBlankQuotation(
  isAutoPx: boolean,
  car: CarItem,
  driver: CustomerItem,
): Quotation {
  const unitDriver: Driver = {
    ...makeEmptyDriver(),
    firstName: driver.firstName,
    lastName: driver.lastName,
    gender: driver.gender,
    birthday: driver.birthday,
    transitZone: driver.transitZone,
    transitTime: driver.transitTime,
    foulsQty: driver.collisionsQty.toString(),
    alcoholicBreath: driver.alcoholBreath.toString(),
    civilStatus: '',
    nationality: '',
  };

  const customer: Customer = {
    ...makeEmptyCustomer(),
    firstName: driver.firstName,
    lastName: driver.lastName,
    birthday: driver.birthday,
    gender: driver.gender,
    email: driver.email,
    phone: driver.phoneNumber,
    id: driver.id,
    address: '',
    idType: '',
  };

  const vehicle: Vehicle = {
    ...makeEmptyVehicle(),
    brandId: car.brand,
    modelId: car.model.id,
    modelGroupId: car.model.groupId as string | undefined,
    value: car.value,
    modelYear: car.year,
    isNewCar: car.isNewCar || false,
    use: '',
    type: '',
    passengersQty: 0,
    driver: unitDriver,
  };

  const unit: QuotationUnit = {
    ...makeEmptyQuotationUnit(),
    unit: vehicle,
  };
  return {
    ...makeEmptyQuotation(),
    units: [unit],
    customer,
    isAutoPx,
  };
}

/**
 * Returns the default unit represented as a CarItem
 *
 * @param quotation - The quotation where the unit will be extracted
 */
export function defaultUnitAsCarItem(quotation: Quotation): CarItem {
  const vehicle = defaultUnitEntity(quotation);
  return {
    cylinder: '',
    fuel: '',
    hasBill: false,
    passengersQty: `${vehicle.passengersQty}`,
    serialChassis: '',
    serialMotor: '',
    treasuryValue: '',
    brand: vehicle.brandId,
    model: {
      id: vehicle.modelId,
      groupId: '1',
      name: quotation.summary.carBrand,
      brand: quotation.summary.carModel,
    },
    year: vehicle.modelYear,
    value: vehicle.value,
    isNewCar: vehicle.isNewCar,
    typeOfMoney: '',
    licensePlate: '',
    category: '',
    use: vehicle.use,
    color: '',
    noLicensePlate: true,
  };
}

export function hasUnits(quotation: Quotation): boolean {
  return quotation.units.length > 0;
}

/**
 * Since dynamic quotation can freely change between product types, and the values must
 * be retained, we need to remove values that are not valid for the selected product
 * type before doing any requests.
 *
 * @param simplifiedCoverages - A list of simplified coverages present in the quotation
 * @param resumes - A list of coverage resumes describing the catalog for the current product
 */
export function purgeUnwantedSimplifiedCoverages(
  simplifiedCoverages: QuotationCoverage[],
  resumes: CoverageCatalogResume[],
): QuotationCoverage[] {
  const validCodes = resumes.map((coverage): string => coverage.code);
  return simplifiedCoverages.filter((simplifiedCoverage): boolean =>
    validCodes.includes(simplifiedCoverage.coverageId),
  );
}

export function canShowQuotationMonthlyFees(quotation: Quotation): boolean {
  return quotation.productId !== THIRD_PARTY_PACK;
}

export function getQuotationCustomerName(quotation: Quotation): string {
  const { customer, customerType, legalEntity } = quotation;
  if (customerType === NATURAL_PERSON) {
    return `${customer.firstName} ${customer.lastName}`;
  }
  return legalEntity?.name || '';
}

export function shouldShowTotalAmount(
  quotationSummary: QuotationSummaryResponse,
): boolean {
  return quotationSummary.productType === COMPLETE_VEHICLE;
}

/**
 * There are several ocassions where we need to render multiple units, but since the Unit entity has
 * no unique identifier, we need to encapsulate it inside a custom obejct that actually does.
 *
 * @param quotation - The quotation where the units will be extracted from
 */
export function mapUnitsToUniqueId(quotation: Quotation): UniqueIdUnit[] {
  return quotation.units.map(
    (unit): UniqueIdUnit => ({
      id: uniqueId(),
      unit,
    }),
  );
}

/**
 * Returns the description of the selected endorsment
 * @param coverages
 */
export function getSelectedEndorsment(coverages: QuotationCoverage[]): string {
  if (coverages.find((coverage) => coverage.coverageId === GOLD_ENDORSEMENT)) {
    return 'Dorado Plus';
  }
  if (coverages.find((coverage) => coverage.coverageId === PLAT_ENDORSEMENT)) {
    return 'Platino';
  }

  if (
    coverages.find((coverage) => coverage.coverageId === PLAT_PLUS_ENDORSEMENT)
  ) {
    return 'Platino Plus';
  }
  return '';
}

/**
 * Returns if the car is new or used
 * @param isNewCar
 */
export function getCarState(isNewCar: boolean): string {
  return isNewCar ? 'New' : 'Used';
}
